import type { AttachmentItem } from "@outpave/ds";
import { mimeForExtension } from "utils/files/uploadFileTypes";

/**
 * The least an asset has to carry to be shown. Structural rather than a named
 * app type: admin's `ImageAsset` and employee's `ReceiptAsset` differ on
 * nullability, and a util should not import a type out of a component file to
 * find that out.
 */
export interface AttachmentSource {
  id: string;
  assetUrl?: string | null;
  extension?: string | null;
  name?: string | null;
}

/**
 * Map our assets onto the package's `AttachmentItem`.
 *
 * Lossy on purpose, and one-way. `ImageAsset` carries `parentId`,
 * `subParentId`, `description`, `size` and `receiptDetailId`, which feed
 * mutations and have no display meaning; keep the domain objects keyed by id
 * and look them up in the callbacks rather than trying to round-trip through
 * this.
 *
 * `name` is required by the package but optional for us, so the positional
 * "{fileNameDefault} {index + 1}" the old list always rendered becomes the
 * fallback rather than the rule.
 *
 * Which of the two you get depends on the source, and it is not a preference:
 * `Attachment` carries `name` and its queries select it, so those surfaces show
 * the real filename. The server's `ReceiptAsset` is `{ assetUrl, id, extension }`
 * with no name field at all, so every receipt surface takes the fallback and
 * will keep doing so until the schema grows one. Real filenames are stored (the query asks for
 * `name`, and uploads set it from `file.name`), and the package surfaces them
 * in both the row and the viewer's title chip, so showing "Receipt 1" for a
 * file actually called "starbucks-jan-15.pdf" was throwing away information the
 * old list happened not to display.
 */
export const toAttachmentItems = (
  assets: AttachmentSource[],
  fileNameDefault: string,
): AttachmentItem[] =>
  assets.map((asset, index) => ({
    id: asset.id,
    name: asset.name?.trim() || `${fileNameDefault} ${index + 1}`,
    url: asset.assetUrl ?? undefined,
    // Same source: our assets carry no separate thumbnail, and the package
    // needs previewUrl set for a tile to render an image at all.
    previewUrl: asset.assetUrl ?? undefined,
    mime: mimeForExtension(asset.extension),
  }));
